import { useState, useEffect, useCallback } from "react";
import {
  listSmartBidValidationProfiles,
  getSmartBidValidationSummary,
  runSmartBidValidationProfile,
  runAllSmartBidValidationProfiles,
  resetSmartBidValidationProfile,
} from "../lib/adminApi";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  RefreshCw, CheckCircle2, AlertTriangle, XCircle, MinusCircle,
  Play, Eye, RotateCcw, ShieldCheck, TrendingUp, PackageCheck, HeartPulse,
} from "lucide-react";

const STATUS_META = {
  PASS: { label: "PASS", color: "#047857", bg: "#ECFDF5", border: "#047857", Icon: CheckCircle2 },
  WARNING: { label: "WARNING", color: "#B45309", bg: "#FFFBEB", border: "#B45309", Icon: AlertTriangle },
  FAIL: { label: "FAIL", color: "#B91C1C", bg: "#FEF2F2", border: "#B91C1C", Icon: XCircle },
  NOT_RUN: { label: "NOT RUN", color: "#6B7280", bg: "#F3F4F6", border: "#9CA3AF", Icon: MinusCircle },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.NOT_RUN;
  const { Icon } = m;
  return (
    <span
      data-testid={`sbvp-status-${status}`}
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
      style={{ color: m.color, background: m.bg, border: `1px solid ${m.border}` }}
    >
      <Icon size={14} /> {m.label}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, sub, tone = "#0F172A", testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-slate-500 text-xs font-medium uppercase tracking-wide">
        <Icon size={14} /> {label}
      </div>
      <div className="mt-1.5 text-2xl font-bold" style={{ color: tone }}>{value}</div>
      {sub ? <div className="text-xs text-slate-400 mt-0.5">{sub}</div> : null}
    </div>
  );
}

export default function SmartBidValidationProfilesPage() {
  const [profiles, setProfiles] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, sum] = await Promise.all([
        listSmartBidValidationProfiles(),
        getSmartBidValidationSummary(),
      ]);
      setProfiles(rows || []);
      setSummary(sum || null);
    } catch (e) {
      toast.error("Failed to load SmartBid validation profiles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAll = async () => {
    setRunning(true);
    try {
      const sum = await runAllSmartBidValidationProfiles();
      setSummary(sum);
      await load();
      toast.success(`Ran ${sum.total} profiles · ${sum.pass} PASS / ${sum.warning} WARNING / ${sum.fail} FAIL`);
    } catch (e) {
      toast.error("Run all failed");
    } finally {
      setRunning(false);
    }
  };

  const runOne = async (id) => {
    setBusyId(id);
    try {
      await runSmartBidValidationProfile(id);
      await load();
      toast.success("Profile re-validated");
    } catch (e) {
      toast.error("Run failed");
    } finally {
      setBusyId(null);
    }
  };

  const resetOne = async (id) => {
    setBusyId(id);
    try {
      await resetSmartBidValidationProfile(id);
      await load();
      toast.success("Latest result reset");
    } catch (e) {
      toast.error("Reset failed");
    } finally {
      setBusyId(null);
    }
  };

  const health = summary?.health;
  const healthTone = health === "healthy" ? "#047857" : health === "at_risk" ? "#B91C1C" : "#6B7280";

  return (
    <div data-testid="smartbid-validation-profiles-page" className="p-6 max-w-[1200px] mx-auto">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck size={22} className="text-indigo-600" /> SmartBid Validation Profiles
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Permanent regression protection for the SmartBid pipeline — canonical validation →
            conversion → shared engine → recommendation. Runs on stored benchmark fixtures (no live extraction).
          </p>
        </div>
        <Button data-testid="sbvp-run-all-btn" onClick={runAll} disabled={running}>
          {running ? <RefreshCw size={16} className="animate-spin mr-2" /> : <Play size={16} className="mr-2" />}
          Run All
        </Button>
      </div>

      {/* Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        <StatCard testid="sbvp-stat-total" icon={PackageCheck} label="Total Profiles" value={summary?.total ?? "—"} />
        <StatCard testid="sbvp-stat-pass" icon={CheckCircle2} label="Pass" value={summary?.pass ?? "—"} tone="#047857" />
        <StatCard testid="sbvp-stat-warning" icon={AlertTriangle} label="Warning" value={summary?.warning ?? "—"} tone="#B45309" />
        <StatCard testid="sbvp-stat-fail" icon={XCircle} label="Fail" value={summary?.fail ?? "—"} tone="#B91C1C" />
        <StatCard testid="sbvp-stat-notrun" icon={MinusCircle} label="Not Run" value={summary?.not_run ?? "—"} />
        <StatCard testid="sbvp-stat-rec" icon={TrendingUp} label="Recommendation Accuracy" value={summary?.recommendation_accuracy != null ? `${summary.recommendation_accuracy}%` : "—"} />
        <StatCard testid="sbvp-stat-conv" icon={CheckCircle2} label="Conversion Success" value={summary?.conversion_success != null ? `${summary.conversion_success}%` : "—"} />
        <StatCard testid="sbvp-stat-health" icon={HeartPulse} label="Validation Health" value={health ? health.replace("_", " ") : "—"} tone={healthTone}
          sub={summary?.last_run ? `Last run ${new Date(summary.last_run).toLocaleString()}` : "Not run yet"} />
      </div>

      {/* Profiles table */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="sbvp-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Code</th>
              <th className="text-left px-4 py-3">Profile</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Recommendation</th>
              <th className="text-left px-4 py-3">Scenario</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
            ) : profiles.map((p) => {
              const r = p.latest_result || {};
              return (
                <tr key={p.id} data-testid={`sbvp-row-${p.profile_code}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono font-semibold text-slate-700">{p.profile_code}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{p.name}</div>
                    <div className="text-xs text-slate-400 max-w-md truncate">{p.description}</div>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={r.status || "NOT_RUN"} /></td>
                  <td className="px-4 py-3 text-slate-700">{r.recommendation || (p.expected?.should_fail_validation ? "— (safely rejected)" : "—")}</td>
                  <td className="px-4 py-3 text-slate-600 capitalize">{r.scenario || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button data-testid={`sbvp-view-${p.profile_code}`} variant="ghost" size="sm" onClick={() => setDetail(p)} title="View comparison">
                        <Eye size={15} />
                      </Button>
                      <Button data-testid={`sbvp-run-${p.profile_code}`} variant="ghost" size="sm" disabled={busyId === p.id} onClick={() => runOne(p.id)} title="Run">
                        {busyId === p.id ? <RefreshCw size={15} className="animate-spin" /> : <Play size={15} />}
                      </Button>
                      <Button data-testid={`sbvp-reset-${p.profile_code}`} variant="ghost" size="sm" disabled={busyId === p.id} onClick={() => resetOne(p.id)} title="Reset latest result">
                        <RotateCcw size={15} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Comparison modal */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="sbvp-comparison-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.profile_code} · {detail?.name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <p className="text-slate-500">{detail.description}</p>
              <div className="flex items-center gap-2">
                <span className="text-slate-500">Latest result:</span>
                <StatusBadge status={detail.latest_result?.status || "NOT_RUN"} />
              </div>

              {detail.expected?.should_fail_validation ? (
                <div className="rounded-lg border border-slate-200 p-3">
                  <div className="font-medium text-slate-700 mb-1">Safety profile</div>
                  <div className="text-slate-600">
                    Expected: canonical validation rejects the malformed payload (no HTTP 500, no misleading recommendation).
                  </div>
                  <div className="mt-2 text-xs">
                    validation_ok = <b>{String(detail.latest_result?.validation_ok)}</b> ·
                    conversion_ok = <b>{String(detail.latest_result?.conversion_ok)}</b> ·
                    http_500 = <b>{String(detail.latest_result?.http_500)}</b>
                  </div>
                  {(detail.latest_result?.validation_errors || []).length > 0 && (
                    <ul className="mt-2 list-disc list-inside text-slate-600">
                      {detail.latest_result.validation_errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="font-semibold text-slate-500">Field</div>
                  <div className="font-semibold text-slate-500">Expected</div>
                  <div className="font-semibold text-slate-500">Actual</div>
                  {[
                    ["Scenario", detail.expected?.scenario, detail.latest_result?.scenario],
                    ["SAP Required", String(detail.expected?.sap_required ?? "—"), String(detail.latest_result?.sap_required ?? "—")],
                    ["Recommendation", detail.expected?.recommendation, detail.latest_result?.recommendation],
                    ["Deployment", detail.expected?.deployment, detail.latest_result?.deployment],
                    ["Warehouse cx", detail.expected?.warehouse_complexity, detail.latest_result?.warehouse_complexity],
                    ["Integration cx", detail.expected?.integration_complexity, detail.latest_result?.integration_complexity],
                    ["Inventory cx", detail.expected?.inventory_complexity, detail.latest_result?.inventory_complexity],
                    ["Throughput cx", detail.expected?.throughput_complexity, detail.latest_result?.throughput_complexity],
                    ["Intelligence preserved", String(detail.expected?.intelligence_preserved ?? "—"), String(detail.latest_result?.intelligence_preserved ?? "—")],
                  ].map(([f, e, a], i) => {
                    const mismatch = e != null && String(e).toLowerCase() !== String(a ?? "").toLowerCase();
                    return (
                      <div key={i} className="contents">
                        <div className="text-slate-600 py-1 border-t border-slate-100">{f}</div>
                        <div className="text-slate-600 py-1 border-t border-slate-100">{e ?? "—"}</div>
                        <div className={`py-1 border-t border-slate-100 ${mismatch ? "text-red-600 font-semibold" : "text-slate-800"}`}>{a ?? "—"}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {(detail.latest_result?.differences || []).length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="font-medium text-amber-800 mb-1">Differences</div>
                  <ul className="list-disc list-inside text-amber-800 text-xs">
                    {detail.latest_result.differences.map((d, i) => (
                      <li key={i}><b>{d.field}</b>: expected “{String(d.expected)}”, got “{String(d.actual)}”</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="text-xs text-slate-400">
                Downstream: implementation {String(detail.latest_result?.implementation_ready)} ·
                ROI {String(detail.latest_result?.roi_ready)} ·
                report {String(detail.latest_result?.report_ready)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
